import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import NavBar from '../../structure/NavBar';
import { NavBarTop } from '../../structure/NavBarTop';
import urls from '../../constants/apiurl';
import ApiCaller from '../../constants/ApiCaller';
import { useSelector, useDispatch } from 'react-redux';
import { updateTopHeader, updateLoading } from '../../../redux/features/common/commonSlice';
import { showDangerToast } from '../../constants/toaster';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import DownloadIcon from '@mui/icons-material/Download';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

const AyrshareManagerView = () => {
  const { userId } = useParams();
  const userData = useSelector((state) => state?.userData);
  const dispatch = useDispatch();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userDetailsData, setUserDetailsData] = useState({});
  const [userDetails, setUserDetails] = useState(null);
  const [socialSelection, setSocialSelection] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [postTab, setPostTab] = useState(0);
  const [search, setSearch] = useState('');
  const [copiedPostId, setCopiedPostId] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [analyticsPostId, setAnalyticsPostId] = useState(null);
  
  useEffect(() => {
    dispatch(updateTopHeader('Ayrshare Manager Details'));
    // Fetch Ayrshare posts from API
    const fetchAyrsharePosts = async () => {
      try {
        dispatch(updateLoading(true));
        setError(null);
        const res = await ApiCaller.get(
          {},
          `/api/admin-service/ayrshare-profile?page=0&limit=10&sortingField=createdAt&order=true&txt=&userId=${userId}`,
          userData?.access_token
        );
        const response = await res.response.json();
        if ((response.status === 200 || response.status === '200' || response.successful === true) && response.data && response.data.items) {
          setPosts(response.data.items);
        } else {
          setPosts([]);
          setError('No posts found.');
        }
      } catch (err) {
        setPosts([]);
        setError('Failed to fetch Ayrshare posts.');
      } finally {
        setLoading(false);
        dispatch(updateLoading(false));
      }
    };
    fetchAyrsharePosts();
  }, [dispatch, userId, userData]);

  useEffect(() => {
    // Use social accounts from navigation state if available
    if (location.state && location.state.activeSocialAccounts && Array.isArray(location.state.activeSocialAccounts)) {
      setSocialSelection(location.state.activeSocialAccounts);
    } else {
      // fallback to API fetch if not passed
      const fetchProfile = async () => {
        //try {
          dispatch(updateLoading(true));
          setError(null);
      };
      fetchProfile();
    }
    // Always fetch user details
    const fetchUserDetails = async () => {
      try {
        dispatch(updateLoading(true));
        const userRes = await ApiCaller.get({}, urls.userDetails + '/' + userId, userData?.access_token);
        const userResponse = await userRes.response.json();
        if ((userResponse.status === 200 || userResponse.status === '200' || userResponse.successful === true) && userResponse.data) {
          setUserDetails(userResponse.data);
          setError(null);
        } else {
          setUserDetails(null);
          setError('No user details found.');
        }
      } catch (error) {
        setUserDetails(null);
        setError('Failed to fetch user details.');
        showDangerToast('Failed to fetch user details.');
      } finally {
        setLoading(false);
        dispatch(updateLoading(false));
      }
    };
    fetchUserDetails();
    // eslint-disable-next-line
  }, [userId, userData, dispatch]);

  const getUserDetails = async (userId) => {
          try {
              dispatch(updateLoading(true))
              let path = urls.userDetails + '/' + userId
              let res = await ApiCaller.get({}, path, userData?.access_token)
              const response = await res.response.json()
              if (response.status == 200) {
                  dispatch(updateLoading(false))
                  setUserDetailsData(response?.data)
              } else {
                  dispatch(updateLoading(false))
                  showDangerToast(response.message)
              }
          } catch (error) {
              dispatch(updateLoading(false))
              console.error(error)
              showDangerToast(error.message)
          }
      }

  // Social platform definitions (icon, name, key)
  const SOCIAL_PLATFORMS = [
    { key: 'bluesky', name: 'Bluesky', icon: require('../../../assets/img/icons/bluesky.png') },
    { key: 'facebook', name: 'Facebook', icon: require('../../../assets/img/icons/facebook.png') },
    { key: 'googlebusiness', name: 'Google Business', icon: require('../../../assets/img/icons/googlebusiness.png') },
    { key: 'instagram', name: 'Instagram', icon: require('../../../assets/img/icons/instagram.png') },
    { key: 'linkedin', name: 'LinkedIn', icon: require('../../../assets/img/icons/linkedin.png') },
    { key: 'pinterest', name: 'Pinterest', icon: require('../../../assets/img/icons/pinterest.png') },
    { key: 'reddit', name: 'Reddit', icon: require('../../../assets/img/icons/reddit.png') },
    { key: 'snapchat', name: 'Snapchat', icon: require('../../../assets/img/icons/snapchat.png') },
    { key: 'telegram', name: 'Telegram', icon: require('../../../assets/img/icons/telegram.png') },
    { key: 'threads', name: 'Threads', icon: require('../../../assets/img/icons/threads.png') },
    { key: 'tiktok', name: 'TikTok', icon: require('../../../assets/img/icons/tiktok.png') },
    { key: 'twitter', name: 'X/Twitter', icon: require('../../../assets/img/icons/twitter.png') },
    { key: 'youtube', name: 'YouTube', icon: require('../../../assets/img/icons/youtube.png') },
  ];

  // Filtered posts based on search (platforms or postIds)
  const filteredPosts = posts.filter(post => {
    const searchLower = search.toLowerCase();
    const platforms = (post.platforms || []).join(', ');
    const postIds = (post.postIds || []).map(p => p.postUrl).join(' ');
    return platforms.toLowerCase().includes(searchLower) || postIds.toLowerCase().includes(searchLower);
  });

  const handleAnalytics = async (post) => {
    setAnalyticsOpen(true);
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    setAnalyticsData(null);
    setAnalyticsPostId(post.id);
    try {
      const res = await ApiCaller.post(
        {
          id: post.id,
          platforms: post.platforms || []
        },
        `/api/admin-service/ayrshare-profile/analytics/post/${userId}`,
        userData?.access_token
      );
      const response = await res.response.json();
      if ((response.status === 200 || response.status === '200' || response.successful === true) && response.data) {
        setAnalyticsData(response.data);
      } else {
        setAnalyticsError('No analytics found.');
      }
    } catch (err) {
      setAnalyticsError('Failed to fetch analytics.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <NavBar />
        <div className="main-content">
          <NavBarTop />
          <Box className="d-flex justify-content-center align-items-center" sx={{ minHeight: '40vh' }}>
            <span>Loading...</span>
          </Box>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <NavBar />
        <div className="main-content">
          <NavBarTop />
          <Box className="d-flex flex-column justify-content-center align-items-center" sx={{ minHeight: '40vh' }}>
            <img width={"25%"} src={require("../../../assets/img/no-data.gif")} alt="No Data" />
            <br />
            <label style={{ fontWeight: 'bold' }}>{error}</label>
          </Box>
        </div>
      </>
    );
  }

  return (
    <>
      <NavBar />
      <div className="main-content" style={{ background: '#f5f6fa', minHeight: '100vh' }}>
        <NavBarTop />
        <Box sx={{ p: { xs: 1, md: 3 } }}>
          {/* User Details Top Card */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h5" fontWeight={600} gutterBottom>User Details</Typography>
              <Divider sx={{ mb: 2 }} />
              {userDetails ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Full Name</Typography>
                    <Typography>{userDetails.fullName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Username</Typography>
                    <Typography>{userDetails.userName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Email</Typography>
                    <Typography>{userDetails.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Mobile</Typography>
                    <Typography>{userDetails.ext} {userDetails.contactNumber}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Gender</Typography>
                    <Typography>{userDetails.gender}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Date Of Birth</Typography>
                    <Typography>{userDetails.dob}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Date Of Registration</Typography>
                    <Typography>{userDetails.createdAt}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Account Type</Typography>
                    <Typography>{userDetails.accountType}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Total Followers</Typography>
                    <Typography>{userDetails.totalFollowers}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Total Following</Typography>
                    <Typography>{userDetails.totalFollowing}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Total Videos</Typography>
                    <Typography>{userDetails.totalVideos}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Total Likes</Typography>
                    <Typography>{userDetails.totalLikes}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Bio</Typography>
                    <Typography>{userDetails.bio}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">User Preferred Tags</Typography>
                    <Typography>{userDetails.userPreferredTags?.join(', ')}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Profile Pic</Typography>
                    <img src={userDetails.profilePic} alt="Profile" style={{ width: 80, height: 80, borderRadius: 8 }} />
                  </Grid>
                </Grid>
              ) : (
                <Typography>No user details found.</Typography>
              )}
            </CardContent>
          </Card>
          {/* Social Accounts Section as Accordion with Tab-like Buttons and Post Options */}
          <Accordion defaultExpanded sx={{ mb: 3, borderRadius: 2, boxShadow: 0 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ background: '#f5f6fa', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600}>Social Accounts</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Divider sx={{ mb: 2 }} />
              {/* Social Platform Switches (like image) */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Social Networks <Box component="span" sx={{ color: '#bdbdbd', fontWeight: 400, fontSize: 18, ml: 1 }}>ⓘ</Box></Typography>
                <FormGroup row sx={{ flexWrap: 'wrap', gap: 2 }}>
                  {(() => {
                    // Build a set of enabled platform keys from socialSelection
                    const enabledKeys = new Set(
                      socialSelection.map(acc =>
                        (acc.platform || acc.name || '').toLowerCase()
                      )
                    );
                    // Handler for toggling a platform
                    const handleToggle = (platformKey) => {
                      setSocialSelection(prev => {
                        const idx = prev.findIndex(acc => (acc.platform || acc.name || '').toLowerCase() === platformKey);
                        if (idx > -1) {
                          // Remove from selection
                          return prev.filter((_, i) => i !== idx);
                        } else {
                          // Add to selection (as { platform: key })
                          return [...prev, { platform: platformKey }];
                        }
                      });
                    };
                    return SOCIAL_PLATFORMS.map((platform) => {
                      const isEnabled = enabledKeys.has(platform.key);
                      return (
                        <FormControlLabel
                          key={platform.key}
                          control={
                            <Switch
                              checked={isEnabled}
                              onChange={() => handleToggle(platform.key)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: platform.key === 'linkedin' ? '#0a66c2' :
                                         platform.key === 'pinterest' ? '#e60023' :
                                         platform.key === 'snapchat' ? '#fffc00' : '#1976d2',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: platform.key === 'linkedin' ? '#0a66c2' :
                                                   platform.key === 'pinterest' ? '#e60023' :
                                                   platform.key === 'snapchat' ? '#fffc00' : '#1976d2',
                                },
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <img src={platform.icon} alt={platform.name} style={{ width: 20, height: 20, borderRadius: '50%' }} />
                              <Typography sx={{ color: '#222', fontWeight: 600 }}>{platform.name}</Typography>
                            </Box>
                          }
                          labelPlacement="end"
                          sx={{ minWidth: 180, m: 0 }}
                        />
                      );
                    });
                  })()}
                </FormGroup>
              </Box>
              {/* Post Options Section */}
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#222', textAlign: 'center' }}>Post Text</Typography>
                  <TextField
                    multiline
                    minRows={3}
                    fullWidth
                    placeholder="Enter post text"
                    variant="outlined"
                    sx={{ mb: 2, mt: 1, '& .MuiInputBase-root': { fontSize: 16, background: '#fff' },
                          '& .MuiInputBase-input::placeholder': { opacity: 1, color: '#bdbdbd', fontSize: 16 } }}
                    InputProps={{ style: { padding: '10px 14px' } }}
                  />
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#222' }}>Add Images or a Video</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 1 }}>
                      <Button variant="contained" component="label" sx={{ fontWeight: 600, fontSize: 15 }}>
                        UPLOAD FILES
                        <input hidden accept="image/*,video/*" multiple type="file" />
                      </Button>
                      <Button variant="outlined" sx={{ fontWeight: 600, fontSize: 15 }}>USE URLS</Button>
                    </Box>
                    <Box sx={{ color: 'text.secondary', fontSize: 13, textAlign: 'center' }}>
                      Click to Upload or Drag & Drop<br />
                      PNG, JPG, GIF, WEBP, MP4, MOV, AVI up to 50 MB
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#222', textAlign: 'center' }}>Additional Options</Typography>
                  <Card sx={{ p: 2, background: '#f8f9fa', borderRadius: 2, boxShadow: 0 }}>
                    <Typography variant="body2" sx={{ mb: 1, textAlign: 'center', color: '#444' }}>Short Links • Max Pack required<br />Track clicks and demographics</Typography>
                    <TextField
                      select
                      label="Snapchat Post Type"
                      SelectProps={{ native: true }}
                      fullWidth
                      size="small"
                      sx={{ mt: 2, background: '#fff' }}
                    >
                      <option value="story">Story</option>
                      <option value="spotlight">Spotlight</option>
                    </TextField>
                  </Card>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button variant="outlined">Schedule Post</Button>
                <Button variant="contained">Publish Post</Button>
              </Box>
            </AccordionDetails>
          </Accordion>
          {/* Post Tabs, Search, Download */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Tabs
                  value={postTab}
                  onChange={(e, v) => setPostTab(v)}
                  sx={{ minHeight: 36 }}
                >
                  <Tab label="All Posts" sx={{ minHeight: 36 }} />
                  <Tab label="Success" sx={{ minHeight: 36 }} />
                  <Tab label="Scheduled" sx={{ minHeight: 36 }} />
                  <Tab label="Paused" sx={{ minHeight: 36 }} />
                  <Tab label="Awaiting Approval" sx={{ minHeight: 36 }} />
                  <Tab label="Error" sx={{ minHeight: 36 }} />
                </Tabs>
                <TextField
                  size="small"
                  placeholder="Search Posts"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  sx={{ ml: 'auto', width: 220 }}
                />
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  sx={{ ml: 2, whiteSpace: 'nowrap' }}
                >
                  Download Details
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Post Listing */}
          <Box>
            {filteredPosts.length === 0 ? (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography>No posts found.</Typography>
                </CardContent>
              </Card>
            ) : (
              filteredPosts.map(post => (
                <Card key={post.id} sx={{ mb: 2, borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{
                        background: post.status === 'success' ? '#e8f5e9' : post.status === 'deleted' ? '#fff3e0' : '#e3e3e3',
                        color: post.status === 'success' ? '#388e3c' : post.status === 'deleted' ? '#f57c00' : '#222',
                        px: 1.5, py: 0.5, borderRadius: 1, fontWeight: 600, fontSize: 14, display: 'inline-block',
                        mr: 2
                      }}>
                        {post.status}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Post Time: {new Date(post.created).toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Post ID: {post.id}
                      <Button
                        size="small"
                        variant="text"
                        sx={{ minWidth: 0, ml: 1, p: 0.5 }}
                        onClick={() => {
                          navigator.clipboard.writeText(post.id);
                          setCopiedPostId(post.id);
                          setTimeout(() => setCopiedPostId(null), 1200);
                        }}
                        title="Copy Post ID"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </Button>
                      {copiedPostId === post.id && (
                        <span style={{ color: '#388e3c', marginLeft: 8, fontWeight: 600 }}>Copied!</span>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {/* Media preview (play button if video, or just image) */}
                      {post.mediaUrls && post.mediaUrls.length > 0 ? (
                        <Box
                          sx={{
                            width: 120,
                            height: 90,
                            border: '1px dashed #ccc',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fafbfc',
                            cursor: 'pointer',
                            marginRight: 3,
                            position: 'relative',
                            '&:hover .css-play-btn': { background: 'rgba(0,0,0,0.12)' }
                          }}
                          onClick={() => window.open(post.mediaUrls[0], '_blank')}
                        >
                          {/* CSS-only play button */}
                          <Box
                            className="css-play-btn"
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '50%',
                              background: 'rgba(0,0,0,0.08)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'absolute',
                              left: '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                              transition: 'background 0.2s',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: 'block',
                                width: 0,
                                height: 0,
                                borderTop: '12px solid transparent',
                                borderBottom: '12px solid transparent',
                                borderLeft: '20px solid #1976d2',
                                marginLeft: '6px'
                              }}
                            />
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ width: 120, height: 90, borderRadius: 2, marginRight: 3, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="caption">No Media</Typography>
                        </Box>
                      )}
                      <Box sx={{ flex: 1 }}>
                        {/* List all platforms and their post URLs as outlined buttons with icons */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          {(post.postIds || []).map(pid => (
                            <Button
                              key={pid.platform + (pid.id || '')}
                              size="small"
                              variant="outlined"
                              startIcon={<img src={SOCIAL_PLATFORMS.find(p => p.key === pid.platform)?.icon} alt={pid.platform} style={{ width: 18, height: 18 }} />}
                              href={pid.postUrl}
                              target="_blank"
                              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 1.5, py: 0.5 }}
                            >
                              {SOCIAL_PLATFORMS.find(p => p.key === pid.platform)?.name || pid.platform}
                            </Button>
                          ))}
                        </Box>
                      </Box>
                      <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        <Button size="small" variant="outlined">Comments</Button>
                        <Button size="small" variant="outlined" onClick={() => handleAnalytics(post)}>Analytics</Button>
                        <Button size="small" variant="outlined" color="error">Delete</Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        </Box>
      </div>
      {/* Analytics Dialog */}
      <Dialog open={analyticsOpen} onClose={() => setAnalyticsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Analytics</DialogTitle>
        <DialogContent>
          {analyticsLoading ? (
            <Typography>Loading...</Typography>
          ) : analyticsError ? (
            <Typography color="error">{analyticsError}</Typography>
          ) : analyticsData ? (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {Object.entries(analyticsData)
                .filter(([key, pdata]) => key !== 'id' && key !== 'status' && key !== 'errors' && pdata)
                .length === 0 ? (
                  <Typography>No analytics found.</Typography>
                ) : (
                  Object.entries(analyticsData)
                    .filter(([key, pdata]) => key !== 'id' && key !== 'status' && key !== 'errors' && pdata)
                    .map(([platform, pdata]) => (
                      <Box key={platform} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <img src={SOCIAL_PLATFORMS.find(p => p.key === platform)?.icon} alt={platform} style={{ width: 22, height: 22, marginRight: 8 }} />
                          <Typography variant="subtitle1" fontWeight={600}>{SOCIAL_PLATFORMS.find(p => p.key === platform)?.name || platform}</Typography>
                        </Box>
                        {pdata.analytics ? (
                          <Box>{renderAnalyticsValue(pdata.analytics)}</Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No analytics data.</Typography>
                        )}
                        {pdata.postUrl && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">Post URL: <a href={pdata.postUrl} target="_blank" rel="noopener noreferrer">{pdata.postUrl}</a></Typography>
                          </Box>
                        )}
                      </Box>
                    ))
                )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AyrshareManagerView;

// Helper: recursively render analytics fields
const renderAnalyticsValue = (value) => {
  if (value === null || value === undefined) return <span style={{ color: '#bdbdbd' }}>N/A</span>;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return <span style={{ color: '#bdbdbd' }}>[]</span>;
    // Special: mediaUrls array
    if (value[0] && value[0].media && (value[0].media.image || value[0].media.source)) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {value.map((mediaObj, idx) => (
            <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 1 }}>
              {mediaObj.media?.image?.src ? (
                <img src={mediaObj.media.image.src} alt="media" style={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 4, marginBottom: 2 }} />
              ) : null}
              {mediaObj.media?.source ? (
                <Button size="small" variant="outlined" onClick={() => window.open(mediaObj.media.source, '_blank')} sx={{ fontSize: 11, px: 1, py: 0.2, mt: 0.5 }}>Play Video</Button>
              ) : null}
              <Typography variant="caption" sx={{ color: '#888', fontSize: 10 }}>{mediaObj.mediaType || ''}</Typography>
            </Box>
          ))}
        </Box>
      );
    }
    // Otherwise, render array as list
    return (
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {value.map((v, i) => <li key={i}>{renderAnalyticsValue(v)}</li>)}
      </ul>
    );
  }
  if (typeof value === 'object') {
    // Render object as table
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 4, background: '#fafbfc' }}>
        <tbody>
          {Object.entries(value).map(([k, v]) => (
            <tr key={k}>
              <td style={{ padding: 3, fontWeight: 500, color: '#555', verticalAlign: 'top', borderBottom: '1px solid #f0f0f0' }}>{k.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</td>
              <td style={{ padding: 3, borderBottom: '1px solid #f0f0f0' }}>{renderAnalyticsValue(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return <span>{String(value)}</span>;
};
